import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, AlertCircle, Calendar, Zap, Clock } from 'lucide-react';
import { AuthContext } from '../App';
import { useTenant } from '../context/TenantContext';

// Currency symbols map
const currencySymbols = {
  TRY: '₺',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

export default function Upgrade() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const { tenant } = useTenant();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);

  // Fetch pricing configuration from backend
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        setPricingLoading(true);
        const res = await fetch('/api/public/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricingData(data);
        }
      } catch (err) {
        console.error('Failed to load pricing:', err);
      } finally {
        setPricingLoading(false);
      }
    };
    fetchPricing();
  }, []);

  // Get currency from pricing config or tenant settings
  const currency = pricingData?.currency || tenant?.currency || 'TRY';
  const currencySymbol = currencySymbols[currency] || currency;

  const formatPrice = (amount) => {
    // Prices from API are in kuruş/cents — divide by 100
    return `${currencySymbol}${(amount / 100).toLocaleString()}`;
  };

  const getPrice = (planId) => {
    if (planId === 'starter') return 0;
    const planConfig = pricingData?.plans?.[planId];
    if (!planConfig) return 0;
    return billingPeriod === 'annual' ? planConfig.annual_price : planConfig.monthly_price;
  };

  const getSavings = (planId) => {
    if (planId === 'starter' || billingPeriod !== 'annual') return 0;
    const planConfig = pricingData?.plans?.[planId];
    if (!planConfig) return 0;
    const monthlyTotal = planConfig.monthly_price * 12;
    return monthlyTotal - planConfig.annual_price;
  };

  if (!user || !tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('common.accessDenied')}
          </h2>
          <p className="text-gray-600 mb-4">{t('common.pleaseLogIn')}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('auth.login')}
          </button>
        </div>
      </div>
    );
  }

  const plans = [
    {
      id: 'starter',
      name: t('pages.pricing.free'),
      description: t('pages.pricing.freeDesc'),
      features: [
        t('upgrade.starterFeature1'),
        t('upgrade.starterFeature2'),
        t('upgrade.starterFeature3'),
        t('upgrade.starterFeature4'),
      ],
      isCurrent: tenant?.plan === 'starter' || tenant?.plan === 'STARTER',
      canUpgrade: false,
      enabled: pricingData?.plans?.starter?.enabled !== false,
      comingSoon: pricingData?.plans?.starter?.coming_soon || false,
      comingSoonText: pricingData?.plans?.starter?.coming_soon_text,
    },
    {
      id: 'pro',
      name: t('pages.pricing.pro'),
      description: t('pages.pricing.proDesc'),
      popular: true,
      features: [
        t('upgrade.proFeature1'),
        t('upgrade.proFeature2'),
        t('upgrade.proFeature3'),
        t('upgrade.proFeature4'),
        t('upgrade.proFeature5'),
        t('upgrade.proFeature6'),
        t('upgrade.proFeature7'),
      ],
      isCurrent: tenant?.plan === 'pro' || tenant?.plan === 'PRO',
      canUpgrade: (tenant?.plan === 'starter' || tenant?.plan === 'STARTER') && pricingData?.plans?.pro?.enabled && !pricingData?.plans?.pro?.coming_soon,
      enabled: pricingData?.plans?.pro?.enabled || false,
      comingSoon: pricingData?.plans?.pro?.coming_soon || false,
      comingSoonText: pricingData?.plans?.pro?.coming_soon_text,
    },
    {
      id: 'enterprise',
      name: t('pages.pricing.enterprise'),
      description: t('pages.pricing.enterpriseDesc'),
      features: [
        t('upgrade.enterpriseFeature1'),
        t('upgrade.enterpriseFeature2'),
        t('upgrade.enterpriseFeature3'),
        t('upgrade.enterpriseFeature4'),
        t('upgrade.enterpriseFeature5'),
        t('upgrade.enterpriseFeature6'),
        t('upgrade.enterpriseFeature7'),
      ],
      isCurrent: tenant?.plan === 'enterprise' || tenant?.plan === 'ENTERPRISE',
      canUpgrade: (tenant?.plan !== 'enterprise' && tenant?.plan !== 'ENTERPRISE') && pricingData?.plans?.enterprise?.enabled && !pricingData?.plans?.enterprise?.coming_soon,
      enabled: pricingData?.plans?.enterprise?.enabled || false,
      comingSoon: pricingData?.plans?.enterprise?.coming_soon || false,
      comingSoonText: pricingData?.plans?.enterprise?.coming_soon_text,
    },
  ];

  const handleUpgradeClick = async (plan) => {
    if (!plan.canUpgrade || plan.id === 'starter') return;
    setLoading(true);
    setError(null);
    setSelectedPlan(plan.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/payments/upgrade/${tenant.id}/checkout?plan=${plan.id}&billing_period=${billingPeriod}&return_url=${encodeURIComponent(window.location.origin)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL provided');
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      setError(err.message || 'Failed to initiate upgrade');
      setSelectedPlan(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('pages.pricing.title')}</h1>
          <p className="text-xl text-gray-600">{t('pages.pricing.subtitle')}</p>
          <p className="text-sm text-gray-500 mt-4">
            {t('upgrade.currentPlan')}: <span className="font-semibold capitalize">{tenant?.plan?.toLowerCase() || 'starter'}</span>
          </p>
        </div>

        {/* Billing Period Toggle */}
        <div className="flex justify-center mb-10">
          <div className="bg-white rounded-full p-1 shadow-md inline-flex items-center">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                billingPeriod === 'monthly'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Calendar className="w-4 h-4 inline-block mr-2" />
              {t('upgrade.monthly')}
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                billingPeriod === 'annual'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Zap className="w-4 h-4 inline-block mr-2" />
              {t('upgrade.annual')}
              <span className="ml-2 bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">
                {t('upgrade.savePercent', { percent: 17 })}
              </span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg max-w-2xl mx-auto">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3" />
              <div>
                <h3 className="font-semibold text-red-900">{t('upgrade.error')}</h3>
                <p className="text-red-800 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {pricingLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {plans.map((plan) => {
            const price = getPrice(plan.id);
            const savings = getSavings(plan.id);
            
            return (
            <div key={plan.id} className={`relative rounded-2xl transition-all duration-300 ${plan.isCurrent ? 'ring-2 ring-blue-500 bg-white shadow-xl' : 'bg-white shadow-lg hover:shadow-xl'} ${(!plan.enabled && !plan.comingSoon) ? 'opacity-50' : ''}`}>
              {plan.popular && plan.enabled && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <span className="px-4 py-1 bg-blue-600 text-white text-sm font-semibold rounded-full">{t('pages.pricing.proPopular')}</span>
                </div>
              )}
              {plan.comingSoon && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <span className="px-4 py-1 bg-amber-500 text-white text-sm font-semibold rounded-full flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {plan.comingSoonText || t('upgrade.comingSoon', 'Coming Soon')}
                  </span>
                </div>
              )}
              {plan.isCurrent && (
                <div className="absolute top-6 right-6">
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">{t('upgrade.currentPlan')}</span>
                </div>
              )}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 text-sm mb-6">{plan.description}</p>
                
                {/* Pricing */}
                <div className="mb-6">
                  <div className="flex items-baseline">
                    <span className="text-5xl font-bold text-gray-900">{formatPrice(price)}</span>
                    {price > 0 && (
                      <span className="text-gray-600 ml-2">
                        {billingPeriod === 'annual' ? t('upgrade.perYear') : t('upgrade.perMonth')}
                      </span>
                    )}
                    {price === 0 && (
                      <span className="text-gray-600 ml-2">{t('upgrade.forever')}</span>
                    )}
                  </div>
                  {savings > 0 && (
                    <p className="text-green-600 text-sm mt-1 font-medium">
                      {t('upgrade.saveAmount', { amount: formatPrice(savings) })}
                    </p>
                  )}
                  {billingPeriod === 'annual' && price > 0 && (
                    <p className="text-gray-500 text-xs mt-1">
                      {t('upgrade.monthlyWhenBilled', { amount: formatPrice(Math.round(price / 12)) })}
                    </p>
                  )}
                </div>

                {/* CTA Button */}
                {plan.isCurrent ? (
                  <button disabled className="w-full py-3 px-4 bg-gray-100 text-gray-700 font-semibold rounded-lg cursor-not-allowed mb-8">
                    {t('upgrade.currentPlan')}
                  </button>
                ) : plan.comingSoon ? (
                  <button disabled className="w-full py-3 px-4 bg-amber-50 text-amber-600 font-semibold rounded-lg cursor-not-allowed mb-8 flex items-center justify-center gap-2">
                    <Clock className="w-4 h-4" />
                    {plan.comingSoonText || t('upgrade.comingSoon', 'Coming Soon')}
                  </button>
                ) : plan.canUpgrade ? (
                  <button
                    onClick={() => handleUpgradeClick(plan)}
                    disabled={loading && selectedPlan === plan.id}
                    className={`w-full py-3 px-4 font-semibold rounded-lg transition-all mb-8 flex items-center justify-center gap-2 ${
                      loading && selectedPlan === plan.id 
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed' 
                        : plan.popular 
                          ? 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
                          : 'bg-gray-900 text-white hover:bg-gray-800 active:scale-95'
                    }`}
                  >
                    {loading && selectedPlan === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('upgrade.processing')}
                      </>
                    ) : (
                      t('upgrade.upgradeTo', { plan: plan.name })
                    )}
                  </button>
                ) : (
                  <button disabled className="w-full py-3 px-4 bg-gray-100 text-gray-500 font-semibold rounded-lg cursor-not-allowed mb-8">
                    {plan.id === 'starter' ? t('upgrade.freeForever') : t('upgrade.unavailable', 'Unavailable')}
                  </button>
                )}
                <div className="space-y-4">
                  <p className="text-sm font-semibold text-gray-900">{t('upgrade.features')}</p>
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            );
          })}
        </div>
        )}

        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('upgrade.faq')}</h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('upgrade.faqBillingTitle')}</h3>
              <p className="text-gray-600">{t('upgrade.faqBillingDesc')}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('upgrade.faqCancelTitle')}</h3>
              <p className="text-gray-600">{t('upgrade.faqCancelDesc')}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('upgrade.faqDowngradeTitle')}</h3>
              <p className="text-gray-600">{t('upgrade.faqDowngradeDesc')}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('upgrade.faqPaymentTitle')}</h3>
              <p className="text-gray-600">{t('upgrade.faqPaymentDesc')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
